import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { contractsApi, clientsApi, vehiclesApi, formatApiError } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  FileText,
  Plus,
  MagnifyingGlass,
  X,
  Check,
  Warning,
  Calendar,
  User,
  Car,
  CurrencyDollar,
  ChartBar,
  Clock,
  CheckCircle,
  XCircle,
  Trash
} from '@phosphor-icons/react';

const statusColors = {
  activo: 'badge-success',
  vencido: 'badge-danger',
  cancelado: 'badge-neutral',
  reestructurado: 'badge-warning'
};

const statusLabels = {
  activo: 'Activo',
  vencido: 'Vencido',
  cancelado: 'Cancelado',
  reestructurado: 'Reestructurado'
};

const ProgressBar = ({ paid, overdue, total, compact = false }) => {
  const pctPaid = total > 0 ? (paid / total) * 100 : 0;
  const pctOverdue = total > 0 ? (overdue / total) * 100 : 0;
  const h = compact ? 'h-2' : 'h-3';

  return (
    <div data-testid="progress-bar">
      <div className={`w-full ${h} bg-[#F1F5F9] rounded-full overflow-hidden flex`}>
        {pctPaid > 0 && (
          <div
            className="bg-[#10B981] transition-all duration-500"
            style={{ width: `${pctPaid}%` }}
          />
        )}
        {pctOverdue > 0 && (
          <div
            className="bg-[#E11D48] transition-all duration-500"
            style={{ width: `${pctOverdue}%` }}
          />
        )}
      </div>
      {!compact && (
        <div className="flex items-center gap-3 mt-1 text-[10px] text-[#64748B]">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-[#10B981] inline-block" />
            {paid} pagadas
          </span>
          {overdue > 0 && (
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-[#E11D48] inline-block" />
              {overdue} vencidas
            </span>
          )}
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-[#F1F5F9] inline-block border border-[#CBD5E1]" />
            {total - paid - overdue} restantes
          </span>
        </div>
      )}
    </div>
  );
};

const ProgressRing = ({ pct, size = 100, stroke = 8, color = '#10B981' }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const safePct = isNaN(pct) ? 0 : Math.min(100, Math.max(0, pct));
  const offset = circ - (safePct / 100) * circ;
  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="progress-ring">
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#F1F5F9" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="font-bold text-[#0F172A]" style={{ fontSize: size * 0.2 }}>{Math.round(safePct)}%</span>
      </div>
    </div>
  );
};

const Contracts = () => {
  const { canManage } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [formData, setFormData] = useState({
    client_id: '', vehicle_id: '', start_date: '', term_weeks: 52,
    weekly_rent: '', deposit: '', daily_penalty: 100
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [statusModal, setStatusModal] = useState(null);
  const [vehicleModal, setVehicleModal] = useState(null);
  // vehicleModal: { contractId, currentVehicleId, newVehicleId } | null
  // statusModal shape: { contractId, newStatus, date, reason } | null

  useEffect(() => { loadData(); }, []);

  const activeClients = useMemo(
    () => clients.filter(c => c.status === 'activo'),
    [clients]
  );

  const availableVehiclesForSwap = useMemo(
    () => vehicles.filter(v =>
      v.id === vehicleModal?.currentVehicleId || v.status === 'disponible'
    ),
    [vehicles, vehicleModal?.currentVehicleId]
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [contractsRes, clientsRes, vehiclesRes, progressRes] = await Promise.all([
        contractsApi.getAll(),
        clientsApi.getAll(),
        vehiclesApi.getAll(),
        contractsApi.getProgress()
      ]);
      setContracts(contractsRes.data);
      setClients(clientsRes.data);
      setVehicles(vehiclesRes.data);
      setProgress(progressRes.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);
    try {
      await contractsApi.create({
        ...formData,
        weekly_rent: parseFloat(formData.weekly_rent),
        deposit: parseFloat(formData.deposit),
        term_weeks: parseInt(formData.term_weeks),
        daily_penalty: parseFloat(formData.daily_penalty)
      });
      setShowModal(false);
      resetForm();
      loadData();
    } catch (err) {
      setFormError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '', vehicle_id: '', start_date: '', term_weeks: 52,
      weekly_rent: '', deposit: '', daily_penalty: 100
    });
  };

  const handleStatusChange = async (contractId, newStatus) => {
    // For terminal statuses, ask for date + reason
    if (newStatus === 'cancelado' || newStatus === 'reestructurado') {
      setStatusModal({
        contractId,
        newStatus,
        date: new Date().toISOString().slice(0, 10),
        reason: ''
      });
      return;
    }
    try {
      await contractsApi.update(contractId, { status: newStatus });
      setContracts(contracts.map(c => c.id === contractId ? { ...c, status: newStatus } : c));
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const confirmStatusChange = async () => {
    if (!statusModal) return;
    try {
      const payload = {
        status: statusModal.newStatus,
        status_changed_at: statusModal.date,
      };
      if (statusModal.reason && statusModal.reason.trim()) {
        payload.status_change_reason = statusModal.reason.trim();
      }
      await contractsApi.update(statusModal.contractId, payload);
      setStatusModal(null);
      await loadData();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const confirmVehicleChange = async () => {
    if (!vehicleModal || !vehicleModal.newVehicleId) return;
    if (vehicleModal.newVehicleId === vehicleModal.currentVehicleId) {
      setVehicleModal(null);
      return;
    }
    try {
      await contractsApi.update(vehicleModal.contractId, { vehicle_id: vehicleModal.newVehicleId });
      setVehicleModal(null);
      await loadData();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleViewSchedule = async (contract) => {
    try {
      const { data } = await contractsApi.getSchedule(contract.id);
      setSchedule(data);
      setSelectedContract(contract);
      setShowScheduleModal(true);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const fmt = (v) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 }).format(v || 0);

  const getClientName = (id) => clients.find(c => c.id === id)?.name || 'Desconocido';
  const getVehicleInfo = (id) => {
    const v = vehicles.find(v => v.id === id);
    return v ? `${v.brand} ${v.sub_brand} (${v.plates})` : 'Desconocido';
  };

  const availableVehicles = useMemo(() => vehicles.filter(v => v.status === 'disponible'), [vehicles]);

  const filteredContracts = useMemo(() => contracts.filter(c => {
    const s = searchTerm.toLowerCase();
    return getClientName(c.client_id).toLowerCase().includes(s) || getVehicleInfo(c.vehicle_id).toLowerCase().includes(s);
  }), [contracts, searchTerm, clients, vehicles]);

  const selectedProgress = selectedContract ? progress[selectedContract.id] : null;
  const schedulePaid = schedule.filter(s => s.status === 'pagado');
  const scheduleOverdue = schedule.filter(s => s.status === 'pendiente' && s.due_date < new Date().toISOString().slice(0, 10));

  if (loading) return <div className="flex items-center justify-center h-96"><div className="spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="contracts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-[#0F172A]">Contratos</h1>
          <p className="text-[#64748B]">{contracts.length} contratos registrados</p>
        </div>
        {canManage && (
          <button onClick={() => { resetForm(); setShowModal(true); }} className="btn-primary flex items-center gap-2" data-testid="add-contract-btn">
            <Plus size={18} /> Nuevo Contrato
          </button>
        )}
      </div>

      {error && <div className="alert alert-danger flex items-center gap-2"><Warning size={20} />{error}</div>}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="kpi-card"><p className="kpi-label">Total Contratos</p><p className="text-2xl font-bold text-[#0F172A]">{contracts.length}</p></div>
        <div className="kpi-card"><p className="kpi-label">Activos</p><p className="text-2xl font-bold text-[#10B981]">{contracts.filter(c => c.status === 'activo').length}</p></div>
        <div className="kpi-card"><p className="kpi-label">Cartera Total</p><p className="text-xl font-bold text-[#0055FF]">{fmt(contracts.filter(c => c.status === 'activo').reduce((s, c) => s + (c.current_balance || 0), 0))}</p></div>
        <div className="kpi-card"><p className="kpi-label">Vehículos Disponibles</p><p className="text-2xl font-bold text-[#F59E0B]">{availableVehicles.length}</p></div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
        <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar por cliente o vehículo..." className="input pl-10" data-testid="search-contracts-input" />
      </div>

      {/* Table with Progress */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="contracts-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Vehículo</th>
                <th>Inicio</th>
                <th>Plazo</th>
                <th>Renta Semanal</th>
                <th>Progreso</th>
                <th>Saldo Actual</th>
                <th>Estatus</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredContracts.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-8 text-[#64748B]">No se encontraron contratos</td></tr>
              ) : filteredContracts.map(contract => {
                const p = progress[contract.id] || {};
                return (
                  <tr key={contract.id} data-testid={`contract-row-${contract.id}`}>
                    <td>
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-[#64748B]" />
                        {getClientName(contract.client_id)}
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Car size={16} className="text-[#64748B]" />
                        {getVehicleInfo(contract.vehicle_id)}
                      </div>
                    </td>
                    <td className="whitespace-nowrap">{contract.start_date}</td>
                    <td className="whitespace-nowrap">{contract.term_weeks} sem</td>
                    <td className="font-medium">{fmt(contract.weekly_rent)}</td>
                    <td style={{ minWidth: '160px' }}>
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <ProgressBar paid={p.paid_weeks || 0} overdue={p.overdue_weeks || 0} total={p.total_weeks || contract.term_weeks} compact />
                        </div>
                        <span className="text-xs font-semibold text-[#0F172A] whitespace-nowrap" data-testid={`progress-pct-${contract.id}`}>
                          {p.pct_paid || 0}%
                        </span>
                      </div>
                      {(p.overdue_weeks || 0) > 0 && (
                        <span className="text-[10px] text-[#E11D48] font-medium">{p.overdue_weeks} sem vencidas</span>
                      )}
                    </td>
                    <td className="font-bold">{fmt(contract.current_balance)}</td>
                    <td>
                      <select
                        value={contract.status || 'activo'}
                        onChange={e => handleStatusChange(contract.id, e.target.value)}
                        className={`text-xs font-medium rounded-full px-2 py-1 border-0 cursor-pointer ${statusColors[contract.status] || 'badge-neutral'}`}
                        data-testid={`status-contract-${contract.id}`}
                      >
                        {Object.entries(statusLabels).map(([val, label]) => (
                          <option key={val} value={val}>{label}</option>
                        ))}
                      </select>
                      {(contract.status === 'cancelado' || contract.status === 'reestructurado') && contract.status_changed_at && (
                        <div
                          className="text-[10px] text-[#64748B] mt-1 whitespace-nowrap"
                          data-testid={`status-date-${contract.id}`}
                          title={contract.status_change_reason || ''}
                        >
                          desde {contract.status_changed_at}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleViewSchedule(contract)}
                          className="p-2 text-[#64748B] hover:text-[#0055FF] hover:bg-blue-50 rounded"
                          title="Ver resumen y calendario"
                          data-testid={`view-schedule-${contract.id}`}
                        >
                          <ChartBar size={18} />
                        </button>
                        {canManage && (
                          <button
                            onClick={() => setVehicleModal({
                              contractId: contract.id,
                              currentVehicleId: contract.vehicle_id,
                              newVehicleId: contract.vehicle_id
                            })}
                            className="p-2 text-[#64748B] hover:text-[#0055FF] hover:bg-blue-50 rounded"
                            title="Cambiar vehículo"
                            data-testid={`change-vehicle-${contract.id}`}
                          >
                            <Car size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Contract Modal */}
      {showModal && (
        <div className="modal-overlay" data-testid="contract-modal">
          <div className="modal-content animate-fade-in max-w-2xl">
            <div className="p-6 border-b border-[#E2E8F0]">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-heading font-bold">Nuevo Contrato</h2>
                <button onClick={() => setShowModal(false)} className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded"><X size={20} /></button>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {formError && <div className="alert alert-danger text-sm">{formError}</div>}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Cliente *</label>
                  <select value={formData.client_id} onChange={e => setFormData({ ...formData, client_id: e.target.value })} className="input" required data-testid="contract-client-select">
                    <option value="">Seleccionar cliente...</option>
                    {activeClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Vehículo *</label>
                  <select
                    value={formData.vehicle_id}
                    onChange={e => {
                      const vehicle = vehicles.find(v => v.id === e.target.value);
                      setFormData({ ...formData, vehicle_id: e.target.value, weekly_rent: vehicle?.weekly_rent || '' });
                    }}
                    className="input" required data-testid="contract-vehicle-select"
                  >
                    <option value="">Seleccionar vehículo...</option>
                    {availableVehicles.map(v => <option key={v.id} value={v.id}>{v.brand} {v.sub_brand} - {v.plates}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Fecha de Inicio *</label>
                  <input type="date" value={formData.start_date} onChange={e => setFormData({ ...formData, start_date: e.target.value })} className="input" required data-testid="contract-date-input" />
                </div>
                <div>
                  <label className="label">Plazo (semanas) *</label>
                  <input type="number" value={formData.term_weeks} onChange={e => setFormData({ ...formData, term_weeks: e.target.value })} className="input" required min="1" data-testid="contract-term-input" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="label">Renta Semanal *</label>
                  <input type="number" value={formData.weekly_rent} onChange={e => setFormData({ ...formData, weekly_rent: e.target.value })} className="input" required min="0" step="0.01" data-testid="contract-rent-input" />
                </div>
                <div>
                  <label className="label">Depósito *</label>
                  <input type="number" value={formData.deposit} onChange={e => setFormData({ ...formData, deposit: e.target.value })} className="input" required min="0" step="0.01" data-testid="contract-deposit-input" />
                </div>
                <div>
                  <label className="label">Penalización Diaria</label>
                  <input type="number" value={formData.daily_penalty} onChange={e => setFormData({ ...formData, daily_penalty: e.target.value })} className="input" min="0" step="0.01" data-testid="contract-penalty-input" />
                </div>
              </div>
              {formData.weekly_rent && formData.term_weeks && (
                <div className="p-4 bg-[#F8FAFC] rounded-lg">
                  <p className="text-sm text-[#64748B]">Saldo inicial: <span className="font-bold text-[#0F172A]">{fmt(parseFloat(formData.weekly_rent) * parseInt(formData.term_weeks))}</span></p>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Cancelar</button>
                <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2" data-testid="save-contract-btn">
                  {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Check size={18} />}
                  Crear Contrato
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Schedule Modal with Visual Summary */}
      {showScheduleModal && selectedContract && (
        <div className="modal-overlay" data-testid="schedule-modal">
          <div className="modal-content animate-fade-in max-w-5xl">
            <div className="p-6 border-b border-[#E2E8F0]">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-heading font-bold">Resumen del Contrato</h2>
                  <p className="text-sm text-[#64748B]">
                    {getClientName(selectedContract.client_id)} &mdash; {getVehicleInfo(selectedContract.vehicle_id)}
                  </p>
                </div>
                <button onClick={() => setShowScheduleModal(false)} className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded"><X size={20} /></button>
              </div>
              {(selectedContract.status === 'cancelado' || selectedContract.status === 'reestructurado') && (
                <div
                  className={`mt-3 px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${
                    selectedContract.status === 'cancelado'
                      ? 'bg-red-50 text-red-800 border border-red-200'
                      : 'bg-yellow-50 text-yellow-800 border border-yellow-200'
                  }`}
                  data-testid="contract-status-banner"
                >
                  <span className="font-semibold uppercase text-xs tracking-wider">
                    {statusLabels[selectedContract.status]}
                  </span>
                  {selectedContract.status_changed_at && (
                    <span className="text-xs">
                      desde <span className="font-semibold">{selectedContract.status_changed_at}</span>
                    </span>
                  )}
                  {selectedContract.status_change_reason && (
                    <span className="text-xs italic">— {selectedContract.status_change_reason}</span>
                  )}
                </div>
              )}
            </div>

            {/* Visual Summary Header */}
            <div className="p-4 sm:p-6 bg-gradient-to-r from-[#F8FAFC] to-white border-b border-[#E2E8F0]" data-testid="contract-visual-summary">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
                {/* Progress Ring */}
                <div className="flex flex-col items-center flex-shrink-0">
                  <ProgressRing
                    pct={selectedProgress?.pct_paid || (schedule.length > 0 ? (schedulePaid.length / schedule.length) * 100 : 0)}
                    size={90}
                    stroke={9}
                    color={
                      (selectedProgress?.pct_paid || 0) >= 75 ? '#10B981' :
                      (selectedProgress?.pct_paid || 0) >= 40 ? '#F59E0B' : '#0055FF'
                    }
                  />
                  <span className="text-xs text-[#64748B] mt-1 font-medium">Avance</span>
                </div>

                {/* KPI Cards */}
                <div className="flex-1 w-full grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="rounded-xl bg-white border border-[#E2E8F0] p-3">
                    <div className="flex items-center gap-1.5 text-[#10B981] mb-1">
                      <CheckCircle size={16} weight="fill" />
                      <span className="text-[10px] font-semibold uppercase tracking-wider">Pagadas</span>
                    </div>
                    <p className="text-xl font-bold text-[#0F172A]" data-testid="summary-paid-weeks">{selectedProgress?.paid_weeks || schedulePaid.length}</p>
                    <p className="text-[11px] text-[#64748B]">de {selectedProgress?.total_weeks || schedule.length} sem</p>
                  </div>
                  <div className="rounded-xl bg-white border border-[#E2E8F0] p-3">
                    <div className="flex items-center gap-1.5 text-[#E11D48] mb-1">
                      <XCircle size={16} weight="fill" />
                      <span className="text-[10px] font-semibold uppercase tracking-wider">Vencidas</span>
                    </div>
                    <p className="text-xl font-bold text-[#0F172A]" data-testid="summary-overdue-weeks">{selectedProgress?.overdue_weeks || scheduleOverdue.length}</p>
                    <p className="text-[11px] text-[#64748B]">sin pago</p>
                  </div>
                  <div className="rounded-xl bg-white border border-[#E2E8F0] p-3">
                    <div className="flex items-center gap-1.5 text-[#0055FF] mb-1">
                      <CurrencyDollar size={16} weight="fill" />
                      <span className="text-[10px] font-semibold uppercase tracking-wider">Cobrado</span>
                    </div>
                    <p className="text-base font-bold text-[#0F172A]" data-testid="summary-total-paid">{fmt(selectedProgress?.total_paid || schedulePaid.reduce((s, p) => s + (p.paid_amount || 0), 0))}</p>
                    <p className="text-[11px] text-[#64748B]">de {fmt(selectedProgress?.total_expected || selectedContract.initial_balance)}</p>
                  </div>
                  <div className="rounded-xl bg-white border border-[#E2E8F0] p-3">
                    <div className="flex items-center gap-1.5 text-[#F59E0B] mb-1">
                      <Clock size={16} weight="fill" />
                      <span className="text-[10px] font-semibold uppercase tracking-wider">Restante</span>
                    </div>
                    <p className="text-base font-bold text-[#0F172A]" data-testid="summary-remaining">{fmt(selectedContract.current_balance)}</p>
                    <p className="text-[11px] text-[#64748B]">{Math.max(0, (selectedProgress?.total_weeks || schedule.length) - (selectedProgress?.paid_weeks || schedulePaid.length) - (selectedProgress?.overdue_weeks || scheduleOverdue.length))} sem pendientes</p>
                  </div>
                </div>
              </div>

              {/* Full progress bar */}
              <div className="mt-5">
                <ProgressBar
                  paid={selectedProgress?.paid_weeks || schedulePaid.length}
                  overdue={selectedProgress?.overdue_weeks || scheduleOverdue.length}
                  total={selectedProgress?.total_weeks || schedule.length}
                />
              </div>
            </div>

            {/* Schedule Table */}
            <div className="p-6 max-h-[45vh] overflow-y-auto">
              <h3 className="text-sm font-bold text-[#0F172A] uppercase tracking-wider mb-3">Calendario de Pagos</h3>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Semana</th>
                    <th>Fecha Vencimiento</th>
                    <th>Monto Esperado</th>
                    <th>Monto Pagado</th>
                    <th>Días Atraso</th>
                    <th>Penalización</th>
                    <th>Estatus</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.map(payment => {
                    const today = new Date().toISOString().slice(0, 10);
                    const isOverdue = payment.status === 'pendiente' && payment.due_date < today;
                    return (
                      <tr key={payment.id} className={isOverdue ? 'bg-red-50/50' : ''} data-testid={`schedule-row-${payment.id}`}>
                        <td className="font-medium">Semana {payment.week_number}</td>
                        <td>{payment.due_date}</td>
                        <td>{fmt(payment.expected_amount)}</td>
                        <td className={payment.paid_amount > 0 ? 'text-[#10B981] font-medium' : ''}>
                          {fmt(payment.paid_amount)}
                        </td>
                        <td className={payment.days_late > 0 ? 'text-[#E11D48]' : ''}>
                          {payment.days_late || 0}
                        </td>
                        <td className={payment.penalty_final > 0 ? 'text-[#E11D48]' : ''}>
                          {fmt(payment.penalty_final || payment.penalty_calculated || 0)}
                        </td>
                        <td>
                          <span className={`badge ${
                            payment.status === 'pagado' ? 'badge-success' :
                            isOverdue ? 'badge-danger' : 'badge-warning'
                          }`}>
                            {payment.status === 'pagado' ? 'Pagado' :
                             isOverdue ? 'Vencido' : 'Pendiente'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Modal (cancelado / reestructurado) */}
      {statusModal && (
        <div className="modal-overlay" data-testid="status-change-modal">
          <div className="modal-content animate-fade-in max-w-md">
            <div className="p-6 border-b border-[#E2E8F0]">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-heading font-bold">
                  Marcar como {statusLabels[statusModal.newStatus]}
                </h2>
                <button
                  onClick={() => setStatusModal(null)}
                  className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {statusModal.newStatus === 'cancelado' && (
                <div className="alert alert-danger text-xs">
                  Al cancelar, todas las semanas pendientes posteriores a esta fecha quedarán marcadas como canceladas y no generarán mora.
                </div>
              )}
              <div>
                <label className="label">Fecha de {statusModal.newStatus === 'cancelado' ? 'cancelación' : 'reestructura'} *</label>
                <input
                  type="date"
                  value={statusModal.date}
                  onChange={(e) => setStatusModal({ ...statusModal, date: e.target.value })}
                  className="input"
                  required
                  data-testid="status-date-input"
                />
              </div>
              <div>
                <label className="label">Motivo (opcional)</label>
                <textarea
                  value={statusModal.reason}
                  onChange={(e) => setStatusModal({ ...statusModal, reason: e.target.value })}
                  className="input"
                  rows={3}
                  placeholder="Ej. Incumplimiento de pago / renegociación de plazo"
                  data-testid="status-reason-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setStatusModal(null)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirmStatusChange}
                  disabled={!statusModal.date}
                  className="btn-primary flex items-center gap-2"
                  data-testid="confirm-status-btn"
                >
                  <Check size={18} />
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Vehicle Modal */}
      {vehicleModal && (
        <div className="modal-overlay" data-testid="vehicle-change-modal">
          <div className="modal-content animate-fade-in max-w-md">
            <div className="p-6 border-b border-[#E2E8F0]">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-heading font-bold">Cambiar Vehículo</h2>
                <button
                  onClick={() => setVehicleModal(null)}
                  className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="alert alert-warning text-xs">
                Al cambiar el vehículo, todos los pagos y calendario del contrato quedarán asociados al vehículo nuevo. El vehículo anterior se marcará como disponible.
              </div>
              <div>
                <label className="label">Vehículo actual</label>
                <input
                  type="text"
                  value={getVehicleInfo(vehicleModal.currentVehicleId)}
                  className="input bg-[#F8FAFC]"
                  disabled
                  data-testid="current-vehicle-display"
                />
              </div>
              <div>
                <label className="label">Nuevo vehículo *</label>
                <select
                  value={vehicleModal.newVehicleId || ''}
                  onChange={(e) => setVehicleModal({ ...vehicleModal, newVehicleId: e.target.value })}
                  className="input"
                  required
                  data-testid="new-vehicle-select"
                >
                  <option value="">Seleccionar...</option>
                  {/* Include current vehicle (already assigned) + all available */}
                  {vehicles.filter(v => v.id === vehicleModal.currentVehicleId || v.status === 'disponible').map(v => (
                    <option key={v.id} value={v.id}>
                      {v.brand} {v.sub_brand} ({v.plates}) {v.id === vehicleModal.currentVehicleId ? '— Actual' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-[#64748B] mt-1">
                  Solo se listan vehículos disponibles más el actual.
                </p>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setVehicleModal(null)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirmVehicleChange}
                  disabled={!vehicleModal.newVehicleId || vehicleModal.newVehicleId === vehicleModal.currentVehicleId}
                  className="btn-primary flex items-center gap-2"
                  data-testid="confirm-vehicle-btn"
                >
                  <Check size={18} />
                  Confirmar cambio
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contracts;
